import { Card, PageHeader, useToast } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ClipboardList, Save } from 'lucide-react';
import { type JSX } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import { createOrder, type OrderCreateInput } from '../../orders/api';
import { isCutoffPassedError, mapOrderError } from '../../orders/order-api-errors';
import { DEFAULT_ORDER_FORM_VALUES, toOrderCreateInput } from '../../orders/order-form-schema';
import { hasPermission } from '../../auth/has-permission';
import { OrderForm } from './components/order-form';

/**
 * `orders/spec.md` → "Alta de pedido" (UI). `/orders/new`, create mode
 * only — edit reuses `OrderForm` from `OrderDetailPage`'s Detalle tab
 * instead (`design.md` §8.1: no separate edit route).
 */
export function OrderFormPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();

  const mutation = useMutation({
    mutationFn: (input: OrderCreateInput) => createOrder(input),
    onSuccess: async (order) => {
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast('success', 'Pedido creado');
      await navigate({ to: '/orders/$orderId', params: { orderId: order.id } });
    },
  });

  const canOverride = hasPermission(currentUser.data, 'order.override.cutoff');
  const cutoffError = isCutoffPassedError(mutation.error);
  const errorMessage = cutoffError ? undefined : mapOrderError(mutation.error);

  return (
    <section className="flex max-w-3xl flex-col gap-6">
      <PageHeader
        icon={ClipboardList}
        title="Nuevo pedido"
        description="Cargá un pedido en nombre del cliente para la fecha de servicio elegida."
      />

      <Card>
        <OrderForm
          mode="create"
          defaultValues={DEFAULT_ORDER_FORM_VALUES}
          submitting={mutation.isPending}
          submitLabel="Guardar borrador"
          submitIcon={Save}
          formError={errorMessage === '' ? undefined : errorMessage}
          cutoffError={cutoffError}
          canOverrideCutoff={canOverride}
          onSubmit={async (values) => {
            await mutation.mutateAsync(toOrderCreateInput(values)).catch(() => undefined);
          }}
        />
      </Card>
    </section>
  );
}
